import {
  Activity,
  AlertTriangle,
  Cpu,
  Database,
  DollarSign,
  Timer,
  Tag,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { type AnalyticsResponse } from '../../services/api/analytics';

interface Props {
  data: AnalyticsResponse | null;
  loading: boolean;
}

const formatUsd = (n: number): string =>
  n.toLocaleString('sv-SE', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatTokens = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('sv-SE');
};

const formatPercent = (frac: number): string => `${Math.round(frac * 100)}%`;

const formatRouteLabel = (routeCanonicalAction: string): string =>
  routeCanonicalAction && routeCanonicalAction.trim() ? routeCanonicalAction : '(unknown)';

export const AnalyticsLlmRunsCards = ({ data, loading }: Props) => {
  const telemetry = data?.llmTelemetry;
  const totals = telemetry?.totals;
  const byModel = telemetry?.byModel ?? [];
  const byRoute = telemetry?.byRoute ?? [];
  const feedback = telemetry?.feedback;
  const unmappedModels = telemetry?.unmappedModels ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          LLM-anrop (telemetri)
        </h3>
        <span className="text-[11px] text-slate-400">Källa: llm_runs · uppdateras nattligt</span>
      </div>

      {!loading && unmappedModels.length > 0 ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">
            <Tag className="h-4 w-4" /> Modeller utan satt prislista
          </div>
          <p className="text-xs text-amber-800 mb-3">
            Dessa modeller har anropats men finns inte i din prislista. Default-priser används tills
            du sätter rätt värden i tabellen <code className="font-mono">model_pricing</code> (sätt
            sedan <code className="font-mono">is_unmapped = false</code>).
          </p>
          <ul className="space-y-1.5">
            {unmappedModels.map((m) => (
              <li
                key={m.modelKey}
                className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-white/60 px-3 py-2 text-xs"
              >
                <span className="font-mono font-semibold text-amber-900">{m.modelKey}</span>
                <span className="text-amber-700">
                  prompt {m.promptPerM} · cached {m.cachedPromptPerM} · completion{' '}
                  {m.completionPerM} (USD/M tokens)
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!loading && telemetry && !telemetry.hasData ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-4 text-sm text-slate-500">
          Inga LLM-anrop loggade än för perioden. Telemetrin börjar fyllas så fort nya chat-anrop
          går genom de instrumenterade OpenAI-helpers.
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Activity className="h-4 w-4" /> Anrop
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '–' : (totals?.runCount ?? 0).toLocaleString('sv-SE')}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totals && totals.runCount > 0 ? `${formatPercent(totals.successRate)} success` : '–'}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <DollarSign className="h-4 w-4" /> Kostnad
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '–' : formatUsd(totals?.costUsd ?? 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Räknat från tokens × prislista</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Cpu className="h-4 w-4" /> Tokens
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading || !totals ? '–' : formatTokens(totals.promptTokens + totals.completionTokens)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totals
              ? `${formatTokens(totals.promptTokens)} in · ${formatTokens(totals.completionTokens)} ut`
              : '–'}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <Database className="h-4 w-4" /> Cache hit
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading || !totals || totals.promptTokens === 0
              ? '–'
              : formatPercent(totals.cacheHitRate)}
          </div>
          <div className="text-xs text-slate-500 mt-1">Av prompt-tokens</div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
            <AlertTriangle className="h-4 w-4" /> Fel
          </div>
          <div className="text-3xl font-bold text-slate-900">
            {loading ? '–' : (totals?.failCount ?? 0).toLocaleString('sv-SE')}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totals && totals.runCount > 0
              ? `${formatPercent(totals.failCount / totals.runCount)} fail rate`
              : '–'}
          </div>
        </div>
      </div>

      {!loading && byModel.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Per modell
          </div>
          <div className="space-y-2">
            {byModel.map((row) => {
              const totalCost = totals?.costUsd ?? 0;
              const pct = totalCost > 0 ? Math.round((row.costUsd / totalCost) * 100) : 0;
              return (
                <div key={row.model} className="flex items-center gap-3">
                  <div className="w-40 flex-shrink-0">
                    <span className="text-xs font-semibold text-slate-700 font-mono">
                      {row.model}
                    </span>
                  </div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-500"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <div className="w-56 flex-shrink-0 text-right text-xs space-x-1">
                    <span className="font-semibold text-slate-800">{formatUsd(row.costUsd)}</span>
                    <span className="text-slate-400">
                      · {row.runCount.toLocaleString('sv-SE')} anrop
                    </span>
                    {row.latencyP95Ms != null ? (
                      <span className="inline-flex items-center gap-0.5 text-slate-500">
                        <Timer className="h-3 w-3" />
                        {row.latencyP95Ms}ms p95
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {!loading && byRoute.length > 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
            Topp routes (efter antal anrop)
          </div>
          <div className="space-y-2">
            {byRoute.slice(0, 10).map((row) => (
              <div key={row.routeCanonicalAction} className="flex items-center gap-3">
                <div className="flex-1 truncate text-xs font-mono text-slate-700">
                  {formatRouteLabel(row.routeCanonicalAction)}
                </div>
                <div className="text-xs text-slate-500 w-28 text-right">
                  {row.runCount.toLocaleString('sv-SE')} anrop
                </div>
                <div className="text-xs text-slate-500 w-24 text-right">
                  {formatUsd(row.costUsd)}
                </div>
                {row.failCount > 0 ? (
                  <div className="text-xs text-red-600 w-20 text-right">{row.failCount} fel</div>
                ) : (
                  <div className="w-20" />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!loading && telemetry?.hasData && feedback ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Feedback per route (tumme upp/ner)
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1 font-semibold text-emerald-600">
                <ThumbsUp className="h-3.5 w-3.5" /> {feedback.totals.up}
              </span>
              <span className="inline-flex items-center gap-1 font-semibold text-red-600">
                <ThumbsDown className="h-3.5 w-3.5" /> {feedback.totals.down}
              </span>
              {feedback.totals.up + feedback.totals.down > 0 ? (
                <span className="text-slate-400">
                  {formatPercent(feedback.totals.up / (feedback.totals.up + feedback.totals.down))}{' '}
                  positiva
                </span>
              ) : null}
            </div>
          </div>
          {feedback.byRoute.length === 0 ? (
            <p className="text-xs text-slate-400">Ingen feedback i perioden än.</p>
          ) : (
            <div className="space-y-2">
              {feedback.byRoute.slice(0, 10).map((row) => {
                const total = row.up + row.down;
                const upPct = total > 0 ? (row.up / total) * 100 : 0;
                return (
                  <div key={row.routeCanonicalAction} className="flex items-center gap-3">
                    <div className="flex-1 truncate text-xs font-mono text-slate-700">
                      {formatRouteLabel(row.routeCanonicalAction)}
                    </div>
                    <div className="h-2 w-full max-w-[160px] overflow-hidden rounded-full bg-red-100">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${upPct}%` }}
                      />
                    </div>
                    <div className="inline-flex w-12 items-center justify-end gap-0.5 text-right text-xs font-semibold text-emerald-600">
                      <ThumbsUp className="h-3 w-3" />
                      {row.up}
                    </div>
                    <div className="inline-flex w-12 items-center justify-end gap-0.5 text-right text-xs font-semibold text-red-600">
                      <ThumbsDown className="h-3 w-3" />
                      {row.down}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
